import { Media } from './media';

export interface ApiResponseData<T = unknown> {
    data?: T;
    database?: DatabaseOperation[];
    requests?: RequestOperation[];
    webhooks?: WebhookOperation[];
}

export interface ApiResponse<T = unknown> {
    data?: T;
    headers?: HeadersInit;
    ok: boolean;
    redirected?: boolean;
    status: number;
    statusText?: string;
    url: string;
    error?: Error;
    bypass?: boolean;
}

export interface ApiBypass {
    disabled?: boolean;
    verb: string;
    url: string;
    response: string;
}

export interface DatabaseOperation {
    duration: number;
    sql: string;
}

export interface RequestOperation {
    duration: number;
    payload: string;
    response: string;
}

export interface WebhookOperation {
    nBrandID: number;
    nEventTypeID: number;
    vars: string[];
    duration: number;
}

export enum UserType {
    Admin = 1,
    Owner = 2,
    TeamMember = 3,
    GroupAdmin = 4,
    Client = 5,
    Assistant = 6
}

export interface UserFlow {
    created: string;
    email: string;
    headerImage: string;
    emailVerifyId: number;
    authId: number;
    hostId: number;
    creditId: number;
    productId: number;
    teamMembers: number;
    products: number;
    hasPaymentProcessor: boolean;
    userFlowSig: string;
}

/**
 * How an "Access Account" request completes. `redirect` means a separate session was spawned on its
 * own host and a new tab is needed; `refreshInPlace` means this session was switched over and
 * nothing opens. The strings are the API's wire values (`AccessAccountModeRule.ToWireValue`).
 */
export type AccessAccountMode = 'redirect' | 'refreshInPlace';

export interface BaseUser {
    brand: number;
    userId: string;
    userType: UserType;
    userName: string;
    firstName: string;
    lastName: string;
    userMedia: string;
    userMediaExt: string;
    superUserId: number;
    superUserType: UserType;
    /**
     * ROBO-345. This session records a super user, but there is NO WAY BACK to them — do not offer
     * "Back to <super user>".
     *
     * True for a session minted by the spawn path (`POST auth/access-account` in `redirect` mode):
     * it opens in its own tab on its own host, and the impersonator's real session is still live in
     * the tab they came from. Reverting here would restore them into a tab they never left, while
     * the original carried on unchanged. Closing the tab is the way out.
     *
     * Carried in the JWT as well as on the session, so it survives a token refresh — otherwise the
     * button reappears the moment the token cycles.
     */
    superNoRevert?: boolean;
    /**
     * ROBO-398. Which mode an "Access Account" request from this caller is expected to complete in.
     *
     * A PREDICTION the server sends ahead of the click, because the client must decide whether to
     * call `window.open` synchronously in the click handler — after an `await` it is outside the
     * user-gesture window and is popup-blocked — while the server does not decide for real until
     * the request arrives.
     *
     * ★ NOT A CONTRACT. Always handle the mode the response actually reports; this only says which
     * one to prepare for. OPTIONAL because an API older than ROBO-398 does not send it, and the
     * absent case must keep opening the tab: an unneeded tab can be closed again, a missing one
     * cannot be opened after the fact.
     */
    accessAccountMode?: AccessAccountMode;
    superUserName: string;
    superFirstName: string;
    superLastName: string;
    superUserMedia: string;
    superUserMediaExt: string;
    superUserMediaObj?: Media;
    sessionKey: string;
    userMediaObj: Media;
    userFlow: UserFlow;
    zendeskJwt: string;
}

export interface User extends BaseUser {
    brandId: string;
    isAdmin?: boolean;
    /**
     * @see AuthResponse.hasPaymentProcessor — carried through verbatim by
     * `buildUserFromAuthResponse`, so `undefined` still means "the API predates the flag". Read it
     * through `hasSessionPaymentProcessor`, never directly.
     */
    hasPaymentProcessor?: boolean;
    isParentBrand?: boolean;
    roles: string[];
    /** @see AuthResponse.canSeeClientPricing */
    canSeeClientPricing?: boolean;
    /**
     * @see AuthResponse.suspended — always a boolean here: `buildUserFromAuthResponse` reads an
     * absent flag as "not suspended".
     */
    suspended?: boolean;
}

export interface AuthResponse extends BaseUser {
    accessToken: string;
    validTo: string;
    refreshToken: string;
    refreshValidTo: string;
    /** API-673: only the frozen mobile `Auth/login` still returns it; nothing on the UX reads it. */
    permaLink?: string;
    /**
     * Resolved role / keyring list from API-464 (`AuthenticatedResponse.roles`).
     * The access token is encrypted and is not decoded on the client.
     */
    roles?: string[];
    /**
     * ROBO-394. Whether this user may be SHOWN client-payment amounts — order totals, line prices,
     * taxes, balances, per-business revenue. Computed by `ClientPricingAccess` on the API, which is
     * the only implementation of the `hideprices` / `cart-showprices` rule.
     *
     * A DISPLAY entitlement, not an authorization boundary: the amounts are still in the payloads
     * this user can fetch, deliberately, because the same retail prices are on the brand's public
     * order form. It exists so the UI hides them consistently in one place instead of re-deriving
     * the rule per surface — which is how ROBO-247 happened.
     *
     * OPTIONAL because an API older than ROBO-394 does not send it. See
     * `isSiteFinancialDataAllowed`, which falls back to deriving the rule from `roles` when this is
     * absent, so a UX/API version skew does not silently defeat the setting either way.
     */
    canSeeClientPricing?: boolean;
    /**
     * UX-837. Whether the signed-in user's PAYING brand — their own brand, or its active parent when
     * the brand pays through it (`parent-payprocessor`) — has an active payment processor
     * (`vw_User.nBrandPaymentAuthID IS NOT NULL`). Legacy's `User.PayingBrandHasPaymentProcessor`,
     * which gates the Payment Types and Pay Invoices links (`inc/begin.asp:484,487`).
     *
     * SESSION-scoped: it answers for the signed-in user's brand, not for any particular order. Order
     * payment surfaces read `Order.paymentProcessorLinked` instead.
     *
     * OPTIONAL because an API older than UX-837 does not send it; absent reads as "has one" (see
     * `hasSessionPaymentProcessor`). A display gate only — the payment endpoints enforce their own.
     */
    hasPaymentProcessor?: boolean;
    /**
     * ROBO-478. Whether this session belongs to the Owner (userType 2) of a suspended business,
     * including one suspended through its parent (the parent rollup). Legacy sends exactly that user
     * to the billing screen at sign-in (`Login/login.asp:75-77`) and restricts nothing else.
     *
     * `true` ONLY for such an Owner. Every other user of a suspended business is refused outright —
     * a 403 `BRAND_SUSPENDED` from login/authorize/refresh, see `services/session-refusal.ts` — so
     * never has a session to carry it; Admins and Admin-super-user sessions always get `false`.
     *
     * A DISPLAY AND ROUTING SIGNAL, NOT AN AUTHORIZATION BOUNDARY. The API enforces suspension; this
     * only decides where the UI lands the Owner.
     *
     * OPTIONAL because an API older than ROBO-478 does not send it. Absent means not suspended.
     */
    suspended?: boolean;
}
